/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useState, useEffect, useRef } from 'react';
import SensesObject from 'model/stats/SensesObject';

interface SensesSelectorProps {
  sensesObj: SensesObject;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onSave: (sensesObj: SensesObject) => void;
  onPassivePerceptionChange: (value: number) => void;
  passivePerception: number;
}

const SensesSelector: React.FC<SensesSelectorProps> = ({
  sensesObj,
  isOpen,
  setIsOpen,
  onSave,
  onPassivePerceptionChange,
  passivePerception
}) => {
  const panelRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {

  }, []);

  useEffect(() => {
    let newSenseObj = structuredClone(sensesObj);
    newSenseObj.passiveperception = passivePerception;
    newSenseObj = SensesObject.buildCustomStr(newSenseObj);
    onSave(newSenseObj);
  }, [sensesObj, passivePerception]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    attribute: keyof SensesObject
  ) => {
    const value =
      event.target.type === 'checkbox'
        ? event.target.checked
        : Math.max(0, parseInt(event.target.value, 10));
    let newSenses = { ...sensesObj, [attribute]: value };
    newSenses = SensesObject.buildCustomStr(newSenses);
    if (attribute === 'passiveperception') {
      onPassivePerceptionChange(value as number);
    }
    onSave(newSenses);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      panelRef.current && !panelRef.current.contains(event.target as Node) &&
      textRef.current && !textRef.current.contains(event.target as Node) &&
      titleRef.current && !titleRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    window.document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Cleanup code when the component is unmounted
      window.document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="property-line">
      <h4 onMouseDown={() => setIsOpen(true)} ref={titleRef}>
        Senses
      </h4>
      &nbsp;
      <span className="multi-select-container edit-field">
        <span className="property-line" onMouseDown={() => setIsOpen(true)} ref={textRef}>
          <span className="property-line">
            {sensesObj.customString.split(',')
                                   .map(sense => sense.replace(/ /g, '\u00A0').replace(/\u00A0\(/g, ' ('))
                                   .join(', ') || '\u00A0 -- \u00A0'}
          </span>
        </span>
        {isOpen && (
          <span className="multi-select-panel senses-select-panel" ref={panelRef}>
            {Object.entries(sensesObj).map(([key, value]) => (
              <div key={key}>
                {key !== 'blindbeyond' && key !== 'customString' && ( 
                  <>
                    <span className="senses-input-key">
                      {key === 'passiveperception' ? "Passive Perception" : 
                      (key.charAt(0).toUpperCase() + key.slice(1))}
                    </span>
                    <input
                      className="senses-input"
                      type="number" step={key === 'passiveperception' ? '1' : '5'}
                      value={value as number}
                      onChange={(event) => handleChange(event, key as keyof SensesObject)}
                    />
                    {key !== 'passiveperception' && " ft."}
                    {(key === 'blindsight' && (value !== 0)) && (
                    <span style={{ paddingLeft: '45px' }}>
                      <input
                        type="checkbox"
                        checked={sensesObj.blindbeyond}
                        onChange={(event) => handleChange(event, 'blindbeyond')}
                      />
                      blind beyond {value}'
                    </span>
                    )}
                  </>
                )}
              </div>
            ))}
          </span>
        )}
      </span>
    </div>
  );
};

export default SensesSelector;
